import { useState } from "react";

import styles from "./insights.module.css";
import { Trans } from "@coworker/locales";
import SkapaTabs from "@coworker/app/src/components/InputPopup/SkapaTabs";
import InsightsTables from "./InsightsTables";
import { InsightsTablesType } from "../hooks/useInsightsData";
import { InsightsRowValueFiltersType } from "../types/insightsFilterType";
import {
  ArticleInsightsType,
  InsightsRowType,
  ItemHfbTypeInsightsType,
  RefilledAreaInsightsType,
  RefilledSalesLocationIDInsightsType,
} from "../types/insightsType";
import { InsightsPeriodTabs } from "../constants/insightsFilters";
import { ProductSearchComponent } from "@coworker/apprestructured/src/scan-and-search/pages/product-search-page/ProductSearchComponent";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { SearchItemCardRenderType } from "@coworker/apprestructured/src/scan-and-search/types/SearchItemCardRenderType";

const Insights = () => {
  const [selectedTab, setSelectedTab] = useState("yesterday");
  const [searchQuery, setSearchQuery] = useState("");
  const [articleSearchInputValue, setArticleSearchInputValue] =
    useState<string>("");
  const tabChangedHandler = (tabKey: string) => {
    setSelectedTab(tabKey);
  };

  const [filters, setFilters] = useState<InsightsRowValueFiltersType>({
    itemNo: "",
    refillAreaId: "",
    itemRefillLocationGridCode: "",
    itemHfbNo: "",
  });

  const handleRowClick = (row: InsightsRowType, table: InsightsTablesType) => {
    // Asserting types here as it is not inherently picked up by typescript in this configuration
    setFilters((prevState: InsightsRowValueFiltersType) => {
      const newFilters = { ...prevState };
      switch (table) {
        case "articleInsights": {
          const articleInsightsRow = row as ArticleInsightsType & {
            itemFamilyName: string;
          };
          if (articleInsightsRow.itemNo !== newFilters.itemNo) {
            setArticleSearchInputValue(articleInsightsRow.itemNo);
            newFilters.itemNo = articleInsightsRow.itemNo;
          } else {
            setArticleSearchInputValue("");
            newFilters.itemNo = "";
          }
          break;
        }
        case "refilledAreaInsights": {
          const refilledAreaRow = row as RefilledAreaInsightsType;
          newFilters.refillAreaId =
            refilledAreaRow.refillAreaId !== newFilters.refillAreaId
              ? refilledAreaRow.refillAreaId
              : "";
          break;
        }
        case "refilledSalesLocationIDInsights": {
          const refilledSalesLocationIDRow =
            row as RefilledSalesLocationIDInsightsType;
          newFilters.itemRefillLocationGridCode =
            refilledSalesLocationIDRow.itemRefillLocationGridCode !==
            newFilters.itemRefillLocationGridCode
              ? refilledSalesLocationIDRow.itemRefillLocationGridCode
              : "";
          break;
        }
        case "itemHfbTypeInsights": {
          const itemHfbTypeRow = row as ItemHfbTypeInsightsType;
          newFilters.itemHfbNo =
            itemHfbTypeRow.itemHfbNo !== newFilters.itemHfbNo
              ? itemHfbTypeRow.itemHfbNo
              : "";
          break;
        }
      }
      return newFilters;
    });
  };

  const tabs = InsightsPeriodTabs.map((period) => ({
    title: <Trans>{period.translationKey}</Trans>,
    key: period.key,
  }));

  const handleItemCardClick = (item: Item) => {
    const newArticleSearchInputValue = item?.no.split("ART")[0] || "";
    setSearchQuery("");
    setArticleSearchInputValue(newArticleSearchInputValue);
    setFilters((prevState: any) => ({
      ...prevState,
      itemNo: newArticleSearchInputValue,
    }));
  };

  return (
    <>
      <div className={styles["insights-filters"]}>
        <ProductSearchComponent
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          shouldHideHistory={true}
          handleItemCardClick={handleItemCardClick}
          itemCardRenderType={SearchItemCardRenderType.button}
          shouldHideSPRItems={false}
          shouldHideLocalCombinationItems={true}
        />
        <SkapaTabs
          className={styles["tabs-margin"]}
          tabs={tabs}
          active={selectedTab}
          onTabChanged={tabChangedHandler}
          noTabPanelPadding
        />
      </div>

      <InsightsTables
        selectedPeriod={selectedTab}
        hasSearchedArticleNo={!!articleSearchInputValue}
        clearArticleSearchInputValue={() => {
          setArticleSearchInputValue("");
          setFilters((prevState: any) => ({
            ...prevState,
            itemNo: "",
          }));
        }}
        filters={filters}
        handleRowClick={handleRowClick}
      />
    </>
  );
};

export default Insights;
